import { useEffect, useMemo } from "react";
import { Alert, App, Card, Checkbox, Col, Form, Row, Space, Spin } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton/Button";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";
import CustomInput from "../../../components/CustomInput/Input";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import {
  clearBankOpsMessages,
  clearDepositLookup,
  fetchDepositLookup,
  fetchDepositBusinesses,
  fetchRepaymentDeals,
  recordManualDeposit,
} from "../../../redux/slices/bankOperationsSlice";
import { getUtrRules, UTR_MAX_LENGTH, toTransactionDateIso } from "../../../helpers/helpers";
import { SHOW_MARK_AS_DUPLICATE } from "../../../constants/manualDuplicate";

const DepositForm = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const selectedBusinessOrgId = Form.useWatch("businessOrgId", form);
  const dispatch = useDispatch();
  const { depositBusinesses, repaymentDeals, depositLookup, submitLoading } = useSelector(
    (state) => state.bankOperations
  );

  useEffect(() => {
    dispatch(fetchDepositBusinesses());
    dispatch(fetchRepaymentDeals());
  }, [dispatch]);

  const businessOptions = useMemo(() => {
    return (depositBusinesses || []).map((item) => {
      const name = item.name || item.orgId;
      const virtualAccountNumber = item.virtualAccountNumber;
      const label = virtualAccountNumber
        ? `${name} – ${virtualAccountNumber}`
        : name;

      return {
        value: item.orgId,
        label,
        type: item.type,
      };
    });
  }, [depositBusinesses]);

  const selectedAccountType = useMemo(() => {
    const selected = businessOptions.find((item) => item.value === selectedBusinessOrgId);
    return selected?.type || null;
  }, [businessOptions, selectedBusinessOrgId]);

  const enterpriseOptions = useMemo(() => {
    const uniq = new Map();
    (repaymentDeals || [])
      .filter((deal) => deal.orgId === selectedBusinessOrgId)
      .forEach((deal) => {
        if (deal.busEntId && !uniq.has(deal.busEntId)) {
          uniq.set(deal.busEntId, {
            value: deal.busEntId,
            label: `${deal.entName || deal.busEntId}`,
          });
        }
      });
    return [...uniq.values()];
  }, [repaymentDeals, selectedBusinessOrgId]);

  const handleBusinessChange = async (businessOrgId) => {
    form.setFieldsValue({ enterpriseBusEntId: undefined });
    dispatch(clearDepositLookup());
    if (!businessOrgId) return;
    await dispatch(fetchDepositLookup(businessOrgId));
  };

  const handleCancel = () => {
    form.resetFields();
    dispatch(clearDepositLookup());
    dispatch(clearBankOpsMessages());
  };

  const onFinish = async (values) => {
    const selectedDeal = (repaymentDeals || []).find(
      (deal) => deal.busEntId === values.enterpriseBusEntId && deal.orgId === values.businessOrgId
    );
    
    const payload = {
      orgId: values.businessOrgId,
      virtualAccountNumber: depositLookup?.virtualAccount?.accNum,
      amount: values.amount,
      utr: values.utr?.trim(),
      transactionDate: toTransactionDateIso(values.transactionDate),
      dealId: selectedDeal?.dealId,
      isDuplicate: Boolean(values.isDuplicate),
    };

    if (!payload.virtualAccountNumber) {
      message.error(
        selectedAccountType === "investor"
          ? "Selected investor does not have a virtual account"
          : "Selected business does not have a virtual account"
      );
      return;
    }

    const result = await dispatch(recordManualDeposit(payload));

    if (recordManualDeposit.fulfilled.match(result)) {
      message.success(
        payload.isDuplicate
          ? "Deposit recorded as duplicate (excluded from wallet and ledger)"
          : "Deposit recorded successfully"
      );
      form.resetFields();
      dispatch(fetchRepaymentDeals());
      dispatch(clearDepositLookup());
      dispatch(clearBankOpsMessages());
      return;
    }

    if (recordManualDeposit.rejected.match(result)) {
      message.error(result.payload || "Failed to record deposit", 5);
      dispatch(clearBankOpsMessages());
    }
  };

  return (
    <Card title="Record deposit">
      <Spin spinning={submitLoading}>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ isDuplicate: false }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Business / Investor"
                name="businessOrgId"
                isRequired
                rules={[{ required: true, message: "Select business or investor" }]}
                showSearch
                placeholder="Select business or investor"
                onChange={handleBusinessChange}
                options={businessOptions}
              />
            </Col>
            {selectedAccountType !== "investor" && (
            <Col xs={24} md={12}>
              <CustomSelect
                label="Enterprise"
                name="enterpriseBusEntId"
                allowClear
                placeholder="Optional"
                options={enterpriseOptions}
              />
            </Col>
            )}
            <Col xs={24} md={12}>
              <CustomInputNumber
                label="Received amount"
                name="amount"
                isRequired
                rules={[{ required: true, message: "Amount is required" }]}
                min={1}
                placeholder="Amount received"
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="UTR / Reference number"
                name="utr"
                isRequired
                normalize={(value) => value?.replace(/\s/g, "")}
                maxLength={UTR_MAX_LENGTH}
                rules={getUtrRules({ label: "UTR / Reference number" })}
                placeholder="UTR123456789"
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomDatePicker
                label="Transaction date"
                name="transactionDate"
                isRequired
                placeholder="Select transaction date"
                disabledDate={(current) => current && current > dayjs().endOf("day")}
                rules={[
                  { required: true, message: "Transaction date is required" },
                  {
                    validator: (_, value) => {
                      if (!value || !value.isAfter(dayjs(), "day")) return Promise.resolve();
                      return Promise.reject(new Error("Future dates are not allowed"));
                    },
                  },
                ]}
              />
            </Col>
            {SHOW_MARK_AS_DUPLICATE && (
              <Col xs={24}>
                <Form.Item name="isDuplicate" valuePropName="checked" style={{ marginBottom: 8 }}>
                  <Checkbox>
                    Mark as duplicate (already posted on another path — do not update wallet or ledger)
                  </Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>

          {depositLookup?.virtualAccount?.accNum && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${selectedAccountType === "investor" ? "Investor" : "Business"} VA: ${depositLookup.virtualAccount.accNum} | Balance: ${depositLookup.virtualAccount.amount ?? 0}`}
            />
          )}

          <Space>
            <CustomButton type="primary" htmlType="submit" loading={submitLoading}>
              Record deposit
            </CustomButton>
            <CustomButton htmlType="button" onClick={handleCancel} disabled={submitLoading}>
              Cancel
            </CustomButton>
          </Space>
        </Form>
      </Spin>
    </Card>
  );
};

export default DepositForm;
